import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Quick scatterplot tutorial for d3.js",
  "description": "",
  "published": "2012-09-28T00:00:00.000Z",
  "redirect_from": ["/blog/quick-scatterplot-tutorial-for-d3-js/swizec/5337"],
  "categories": "d3.js, Github, Graphics, HTML, Scatter plot, Uncategorized, Vector",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-finished.png",
        "alt": "When I code",
        "title": "When I code"
      }}></img></p>
    <p>{`One of the many interesting things `}<a parentName="p" {...{
        "href": "http://github.com",
        "title": "GitHub"
      }}>{`Github`}</a>{` does are punchcards for repositories that can tell you when people work on their code. Unfortunately, they're only per-repository and I was interested in `}<a parentName="p" {...{
        "href": "http://nightowls.swizec.com"
      }}>{`per-user Github punchcards`}</a>{`.`}</p>
    <p>{`So I made my own.`}</p>
    <p>{`Collecting the data was fairly straightforward, finding a simple tutorial/example of a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scatter_plot",
        "title": "Scatter plot"
      }}>{`scatterplot`}</a>{` in `}<a parentName="p" {...{
        "href": "http://d3js.org/"
      }}>{`d3.js`}</a>{` proved to be less than trivial.`}</p>
    <h2 {...{
      "id": "scatterplotting"
    }}>{`Scatterplotting`}</h2>
    <p>{`Drawing a scatterplot is nothing more than distributing data into buckets in a two dimensional space, then drawing a circle based on how many entities ended up in a particular bucket. Adding some colour gives us an extra dimension.`}</p>
    <p>{`For starters, we're going to need some simple HTML and a bit of CSS to make things prettier.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<style>
  .axis path,
  .axis line {
    fill: none;
    stroke: #eee;
    shape-rendering: crispEdges;
  }

  .axis text {
    font-family: sans-serif;
    font-size: 11px;
  }

  .loading {
    font-family: sans-serif;
    font-size: 15px;
  }

  .circle {
    fill: #222;
  }
</style>

<div id="punchcard"></div>

<script src="http://d3js.org/d3.v2.min.js"></script>
<script src="script.js"></script>
`}</code></pre>
    <p>{`The `}<em parentName="p">{`div`}</em>{` is where our scatterplot will end up. It doesn't need any styling since we'll do that with d3 directly, but splashing some CSS on stuff in the graph is going to make things look much better.`}</p>
    <p>{`Going into `}<em parentName="p">{`script.js`}</em>{` we start off by defining the width, height and padding for our graph. Due to d3's magic we'll later be able to change these and have the graph scale itself properly without further intervention.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var w = 940,
  h = 300,
  pad = 20,
  left_pad = 100,
  Data_url = "/data.json";
`}</code></pre>
    <p>{`Next we define our scatterplot`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var svg = d3
  .select("#punchcard")
  .append("svg")
  .attr("width", w)
  .attr("height", h);
`}</code></pre>
    <p>{`This tells d3 that we want to put some svg in the `}<em parentName="p">{`punchcard`}</em>{` div and how big we want it.`}</p>
    <p>{`The next step is defining our `}<em parentName="p">{`x `}{`_`}{`and `}{`_`}{`y`}</em>{` scales. They're helpful for translating data into x,y positions on the graph since they're usually not the same.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var x = d3.scale
    .linear()
    .domain([0, 23])
    .range([left_pad, w - pad]),
  y = d3.scale
    .linear()
    .domain([0, 6])
    .range([pad, h - pad * 2]);
`}</code></pre>
    <p>{`We specified that `}<em parentName="p">{`x `}{`_`}{`coordinates map from values between 0 and 23 to coordinates between the left padding and however big the graph is. Similarly for `}{`_`}{`y`}</em>{` coordinates.`}</p>
    <p>{`D3 will handle everything else for us.`}</p>
    <p>{`Next we should define our axes since graphs without labeled axes are rather useless.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var xAxis = d3.svg.axis().scale(x).orient("bottom"),
  yAxis = d3.svg.axis().scale(y).orient("left");
`}</code></pre>
    <p>{`Essentially we've told the axes to use their corresponding scales and where we want the labels to end up.`}</p>
    <p>{`Now it's finally time to draw something!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`svg
  .append("g")
  .attr("class", "axis")
  .attr("transform", "translate(0, " + (h - pad) + ")")
  .call(xAxis);

svg
  .append("g")
  .attr("class", "axis")
  .attr("transform", "translate(" + (left_pad - pad) + ", 0)")
  .call(yAxis);
`}</code></pre>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-simple-axes.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-simple-axes.png",
          "alt": "Scatterplot simple axes",
          "title": "Scatterplot simple axes"
        }}></img></a></p>
    <p>{`As you can see, d3 was nice enough to figure out on its own how many ticks to draw and where to put them. This usually makes graphs more readable, but in our case we do want all the ticks.`}</p>
    <p>{`All it takes is telling the axes how many ticks we want and while we're at it let's improve the labels as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var xAxis = d3.svg
    .axis()
    .scale(x)
    .orient("bottom")
    .ticks(24)
    .tickFormat(function (d, i) {
      var m = d > 12 ? "p" : "a";
      return d % 12 == 0 ? 12 + m : (d % 12) + m;
    }),
  yAxis = d3.svg
    .axis()
    .scale(y)
    .orient("left")
    .ticks(7)
    .tickFormat(function (d, i) {
      return [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ][d];
    });
`}</code></pre>
    <p>{`The `}<em parentName="p">{`.tickFormat`}</em>{` method allows us to give d3 a function that calculates what a label should look like and we used `}<em parentName="p">{`.ticks`}</em>{` to specify how many ticks we want.`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-labeled-axes.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-labeled-axes.png",
          "alt": "Nicely  labeled scatterplot",
          "title": "Nicely  labeled scatterplot"
        }}></img></a></p>
    <p>{`Much better, but since we'll be loading the data asynchronously, let's tell the user what's going on by placing `}<em parentName="p">{`"Loading ..."`}</em>{` roughly in the middle of the plot.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`svg
  .append("text")
  .attr("class", "loading")
  .text("Loading ...")
  .attr("x", function () {
    return w / 2;
  })
  .attr("y", function () {
    return h / 2 - 5;
  });
`}</code></pre>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-loading.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-loading.png",
          "alt": "Scatterplot loading ...",
          "title": "Scatterplot loading ..."
        }}></img></a></p>
    <p>{`Loading the data can be done with one of d3's many data loading functions.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`d3.json(Data_url, function (punchcard_data) {
`}</code></pre>
    <p>{`Within that function we are now going to draw the actual scatterplot.`}</p>
    <p>{`The data I loaded is organized into triplets `}<em parentName="p">{`[`}{`day, hour, N]`}</em>{` where the combination of `}<em parentName="p">{`day`}</em>{` and `}<em parentName="p">{`hour`}</em>{` tells us where to draw a circle and `}<em parentName="p">{`N`}</em>{` tells us how big it should be.`}</p>
    <p>{`We should define another scale for the radius of the circles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var max_r = d3.max(
    punchcard_data.map(function (d) {
      return d[2];
    })
  ),
  r = d3.scale
    .linear()
    .domain([
      0,
      d3.max(punchcard_data, function (d) {
        return d[2];
      }),
    ])
    .range([0, 12]);
`}</code></pre>
    <p>{`Everything from `}<em parentName="p">{`zero`}</em>{` to `}<em parentName="p">{`max_r`}</em>{` will be mapped to radiuses between `}<em parentName="p">{`0`}</em>{` and `}<em parentName="p">{`12`}</em>{` pixels.`}</p>
    <p>{`To make d3 put some data on our graph we need to tell it to load up our data into the graph and give a transformation that results in circles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`svg.selectAll(".loading").remove();

svg
  .selectAll("circle")
  .data(punchcard_data)
  .enter()
  .append("circle")
  .attr("class", "circle")
  .attr("cx", function (d) {
    return x(d[1]);
  })
  .attr("cy", function (d) {
    return y(d[0]);
  })
  .attr("r", function (d) {
    return r(d[2]);
  });
`}</code></pre>
    <p>{`After removing the `}<em parentName="p">{`"Loading ..." `}{`_`}{`we gave d3 our data, then said that for each datum a `}{`_`}{`circle`}</em>{` should be appended to the graph and given attributes `}<em parentName="p">{`cx, cy`}</em>{` and `}<em parentName="p">{`r`}</em>{` that determine where this circle will be displayed and how big it's going to be.`}</p>
    <p>{`Notice we're using functions to compute these values, but because we're lazy we just rely on d3 doing all of the actual calculations with the `}<em parentName="p">{`x, y`}</em>{` and `}<em parentName="p">{`r`}</em>{` scales we defined earlier.`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-finished1.png"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/09/scatterplot-finished1.png",
          "alt": "The final scatterplot",
          "title": "The final scatterplot"
        }}></img></a></p>
    <p>{`On pictures everything looks great now, but the circles appear very suddenly. Wouldn't it be nice if there was a sexy transition going on?`}</p>
    <p>{`To do that we shove a .`}<em parentName="p">{`transition()`}</em>{` and a `}<em parentName="p">{`.delay(800)`}</em>{` before defining the radius.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`svg
  .selectAll("circle")
  .data(punchcard_data)
  .enter()
  .append("circle")
  .attr("class", "circle")
  .attr("cx", function (d) {
    return x(d[1]);
  })
  .attr("cy", function (d) {
    return y(d[0]);
  })
  .transition()
  .duration(800)
  .attr("r", function (d) {
    return r(d[2]);
  });
`}</code></pre>
    <p>{`Now the circles appear in a lovely 800 millisecond transition.`}</p>
    <p>{`Here's the full javascript code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var w = 940,
  h = 300,
  pad = 20,
  left_pad = 100,
  Data_url = "/data.json";

var svg = d3
  .select("#punchcard")
  .append("svg")
  .attr("width", w)
  .attr("height", h);

var x = d3.scale
    .linear()
    .domain([0, 23])
    .range([left_pad, w - pad]),
  y = d3.scale
    .linear()
    .domain([0, 6])
    .range([pad, h - pad * 2]);

var xAxis = d3.svg
    .axis()
    .scale(x)
    .orient("bottom")
    .ticks(24)
    .tickFormat(function (d, i) {
      var m = d > 12 ? "p" : "a";
      return d % 12 == 0 ? 12 + m : (d % 12) + m;
    }),
  yAxis = d3.svg
    .axis()
    .scale(y)
    .orient("left")
    .ticks(7)
    .tickFormat(function (d, i) {
      return [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ][d];
    });

svg
  .append("g")
  .attr("class", "axis")
  .attr("transform", "translate(0, " + (h - pad) + ")")
  .call(xAxis);

svg
  .append("g")
  .attr("class", "axis")
  .attr("transform", "translate(" + (left_pad - pad) + ", 0)")
  .call(yAxis);

svg
  .append("text")
  .attr("class", "loading")
  .text("Loading ...")
  .attr("x", function () {
    return w / 2;
  })
  .attr("y", function () {
    return h / 2 - 5;
  });

d3.json(Data_url, function (punchcard_data) {
  var max_r = d3.max(
      punchcard_data.map(function (d) {
        return d[2];
      })
    ),
    r = d3.scale
      .linear()
      .domain([
        0,
        d3.max(punchcard_data, function (d) {
          return d[2];
        }),
      ])
      .range([0, 12]);

  svg.selectAll(".loading").remove();

  svg
    .selectAll("circle")
    .data(punchcard_data)
    .enter()
    .append("circle")
    .attr("class", "circle")
    .attr("cx", function (d) {
      return x(d[1]);
    })
    .attr("cy", function (d) {
      return y(d[0]);
    })
    .transition()
    .duration(800)
    .attr("r", function (d) {
      return r(d[2]);
    });
});
`}</code></pre>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/more-on-exploring-correlations-in-r/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/109208243_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/more-on-exploring-correlations-in-r/"
        }}>{`More on Exploring Correlations in R`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/html5-render-urban-population"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_2_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/html5-render-urban-population"
        }}>{`HTML5: Render Urban Population Growth on a 3D World Globe With Three.js and Canvas`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://economistsview.typepad.com/economistsview/2011/03/striking-scatterplot-unemployment-and-investment.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_52_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://economistsview.typepad.com/economistsview/2011/03/striking-scatterplot-unemployment-and-investment.html"
        }}>{`Striking Scatterplot: Unemployment and Investment`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://net.tutsplus.com/tutorials/javascript-ajax/building-and-testing-a-backbone-app/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/109034267_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://net.tutsplus.com/tutorials/javascript-ajax/building-and-testing-a-backbone-app/"
        }}>{`Building and Testing a Backbone App`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=8f16ed4f-3c27-43f6-9df9-c5797a6b4fb8",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      